import { Injectable } from '@angular/core';
import { filter, Observable, Subject, switchMap, take, throwError } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { Store } from '@ngrx/store';
import { environment } from '@environments/environment';
import { ResponseObjectSuccess, ResponseSuccess } from '@interfaces/response-success';
import { selectCredential } from '@store/selectors/authentication.selectors';
import { transformDelimiterSettingUpsert } from '@core/helpers';
import { Credential, ConfiguracionDelimitadorForm, ConfiguracionDelimitador } from '@core/models';
import { selectDelimiterSettingList } from '@store/selectors';

@Injectable({
  providedIn: 'root',
})
export class DelimiterSettingService {
  credential$: Observable<Credential | null>;
  delimiterSettingList$: Observable<ConfiguracionDelimitador[] | null>;

  destroyTab$ = new Subject<void>();

  private creationCompletedSource = new Subject<void>();
  creationCompleted$ = this.creationCompletedSource.asObservable();

  private updateCompletedSource = new Subject<void>();
  updateCompleted$ = this.updateCompletedSource.asObservable();

  private findAllCompletedSource = new Subject<void>();
  findAllCompleted$ = this.findAllCompletedSource.asObservable();

  private closeFormSource = new Subject<void>();
  closeForm$ = this.closeFormSource.asObservable();

  constructor(
    private http: HttpClient,
    private store: Store,
  ) {
    this.credential$ = this.store.select(selectCredential);
    this.delimiterSettingList$ = this.store.select(selectDelimiterSettingList);
  }

  onDestroyTab(): void {
    this.destroyTab$.next();
    this.destroyTab$.complete();
    this.destroyTab$ = new Subject<void>();
  }

  removeCompleted(): void {
    this.closeForm();
  }

  creationCompleted(): void {
    this.creationCompletedSource.next();
    this.closeForm();
  }
  
  updateCompleted(): void {
    this.updateCompletedSource.next();
    this.closeForm();
  }

  findAllCompleted(): void {
    this.findAllCompletedSource.next();
  }

  closeForm(): void {
    this.closeFormSource.next();
  }

  find(id: string): Observable<ResponseObjectSuccess<ConfiguracionDelimitador>> {
    return this.credential$.pipe(
      filter((credential) => credential !== null),
      switchMap((credential) => {
        if (!credential.token && !credential?.granted)
          return throwError(() => new Error('No credential granted'));
        
        const token =
          credential && credential?.token
            ? credential.token
            : credential?.apiKey
              ? credential?.apiKey
              : '';

        return this.http.get<ResponseObjectSuccess<ConfiguracionDelimitador>>(
          `${environment.microMarketing}/api/v1/pos/giftcards/delimiters/${id}`,
          { headers: { Authorization: token } },
        );
      }),
      take(1),
    );
  }

  findAll(): Observable<ResponseSuccess<ConfiguracionDelimitador>> {
    return this.credential$.pipe(
      filter((credential) => credential !== null),
      switchMap((credential) => {
        if (!credential?.granted)
          return throwError(() => new Error('No credential granted'));

        const token =
          credential && credential?.token
            ? credential.token
            : credential?.apiKey
              ? credential?.apiKey
              : '';

        const params = {};

        return this.http.get<ResponseSuccess<ConfiguracionDelimitador>>(
          `${environment.microMarketing}/api/v1/pos/giftcards/delimiters`,
          { 
            headers: { Authorization: token },
            params: { ...params },
          },
          
        );
      }),
      take(1),
    );
  }

  create(delimiterSettingForm: ConfiguracionDelimitadorForm): Observable<ResponseObjectSuccess<ConfiguracionDelimitador>> {
    return this.credential$.pipe(
      filter((credential) => !!credential?.granted),
      switchMap((credential) => {
        const token =
          credential && credential?.token
            ? credential.token
            : credential?.apiKey
              ? credential?.apiKey
              : '';

        return this.http.post<ResponseObjectSuccess<ConfiguracionDelimitador>>(
          `${environment.microMarketing}/api/v1/pos/giftcards/delimiters`,
          transformDelimiterSettingUpsert({ ...delimiterSettingForm }),
          { headers: { Authorization: token } }
        );
      }),
      take(1),
    );
  }

  update(id: string, delimiterSettingForm: ConfiguracionDelimitadorForm): Observable<ResponseObjectSuccess<ConfiguracionDelimitador>> {
    return this.credential$.pipe(
      filter((credential) => !!credential?.granted),
      switchMap((credential) => {
        const token =
          credential && credential?.token
            ? credential.token
            : credential?.apiKey
              ? credential?.apiKey
              : '';

        return this.http.patch<ResponseObjectSuccess<ConfiguracionDelimitador>>(
          `${environment.microMarketing}/api/v1/pos/giftcards/delimiters/${id}`,
          transformDelimiterSettingUpsert({ ...delimiterSettingForm }),
          { headers: { Authorization: token } }
        );
      }),
      take(1),
    );
  }

  remove(id: string): Observable<Response> {
    return this.credential$.pipe(
      filter((credential) => !!credential?.granted),
      switchMap((credential) => {
        const token =
          credential && credential?.token
            ? credential.token
            : credential?.apiKey
              ? credential?.apiKey
              : '';

        return this.http.delete<Response>(
          `${environment.microMarketing}/api/v1/pos/giftcards/delimiters/${id}`,
          { headers: { Authorization: token } }
        );
      }),
      take(1),
    );
  }
}
